import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, TrendingUp, Users, FileSpreadsheet, Plus, ArrowRight, Calendar, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/dashboard/stats?authorization=${token}`);
        setStats(res.data);
      } catch (err) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const seedSampleData = async () => {
    try {
      await axios.post(`${API_URL}/api/seed-sample-data?authorization=${token}`);
      toast.success('Sample rate sheets created!');
      const res = await axios.get(`${API_URL}/api/dashboard/stats?authorization=${token}`);
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to create sample data');
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const formatCurrency = (a) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(a || 0);

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div></div></Layout>;
  }

  const recentList = stats?.recent_comparisons || [];

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Welcome back, {user?.name?.split(' ')[0]}</h1>
            <p className="text-slate-600 mt-1">Here's what's happening with your comparisons</p>
          </div>
          <Link to="/new-comparison">
            <Button className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="new-comparison-btn">
              <Plus className="w-5 h-5" />New SME Comparison
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-primary shadow-sm hover:shadow-md transition-shadow" data-testid="stat-comparisons">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-500">Total Comparisons</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_comparisons || 0}</p></div>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><FileText className="w-6 h-6 text-primary" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-savings">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-500">Total Savings Found</p><p className="text-3xl font-bold text-green-600 mt-1">{formatCurrency(stats?.total_savings)}</p></div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><TrendingUp className="w-6 h-6 text-green-600" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-accent shadow-sm hover:shadow-md transition-shadow" data-testid="stat-ratesheets">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-500">Tariffs & Plans</p><p className="text-3xl font-bold text-slate-900 mt-1">{(stats?.tariffs_count || 0) + (stats?.plans_count || 0)}</p></div>
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><FileSpreadsheet className="w-6 h-6 text-accent" /></div>
              </div>
            </CardContent>
          </Card>

          {user?.role === 'admin' && (
            <Card className="border-l-4 border-l-secondary shadow-sm hover:shadow-md transition-shadow" data-testid="stat-users">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div><p className="text-sm font-medium text-slate-500">Total Users</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats?.users_count || 0}</p></div>
                  <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center"><Users className="w-6 h-6 text-secondary" /></div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="shadow-sm">
            <CardHeader><CardTitle className="text-lg">Quick Actions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Link to="/new-comparison" className="block">
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors"><Plus className="w-5 h-5 text-primary" /></div>
                  <div className="flex-1"><p className="font-medium text-slate-900">New SME Comparison</p><p className="text-sm text-slate-500">Upload a bill and generate report</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-primary transition-colors" />
                </div>
              </Link>
              <Link to="/history" className="block">
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors"><FileText className="w-5 h-5 text-accent" /></div>
                  <div className="flex-1"><p className="font-medium text-slate-900">View All Comparisons</p><p className="text-sm text-slate-500">Search and filter past reports</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-accent transition-colors" />
                </div>
              </Link>
              {user?.role === 'admin' && (
                <>
                  <Link to="/rate-sheets" className="block">
                    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                      <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center group-hover:bg-secondary/30 transition-colors"><FileSpreadsheet className="w-5 h-5 text-green-700" /></div>
                      <div className="flex-1"><p className="font-medium text-slate-900">Manage Rate Sheets</p><p className="text-sm text-slate-500">Upload and edit pricing plans</p></div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-green-700 transition-colors" />
                    </div>
                  </Link>
                  {stats?.tariffs_count === 0 && stats?.plans_count === 0 && (
                    <Button variant="outline" className="w-full mt-4" onClick={seedSampleData} data-testid="seed-data-btn">Load Sample Tariffs & Plans</Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Comparisons</CardTitle>
              <Link to="/history"><Button variant="ghost" size="sm" className="text-primary">View All</Button></Link>
            </CardHeader>
            <CardContent>
              {recentList.length > 0 ? (
                <div className="space-y-4">
                  {recentList.map((item) => {
                    const custName = item.extracted_data?.customer_name || item.bill_file_name;
                    const savings = item.comparison_result?.savings_amount || 0;
                    return (
                      <Link key={item.id} to={`/comparison/${item.id}`} className="block">
                        <div className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-sm transition-all">
                          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center"><FileText className="w-6 h-6 text-slate-500" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900 truncate">{custName}</p>
                              <Badge variant="outline" className="text-xs">{item.report_id}</Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(item.created_at)}</span>
                              <span className="flex items-center gap-1 text-green-600 font-medium"><DollarSign className="w-4 h-4" />{formatCurrency(savings)} saved</span>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No comparisons yet</p>
                  <Link to="/new-comparison"><Button className="mt-4 rounded-full" data-testid="empty-new-comparison-btn">Create Your First Comparison</Button></Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
